/**
 * AgentChatPage — Agent Sessions M1–M5
 *
 * M1: session list, status chip, model badge (provider + model + running tokens/cost)
 * M1: '/' autocomplete lists assigned skills; /skill-name args injects skill content
 * M1: cancel button → chip updates to 'cancelled'
 * M1: on reconnect (reload), transcript re-hydrates from REST identical to stream
 * M4: actor picker using roster endpoint (AC-AGS-M4-3)
 * M4: delegation cards in transcript (AC-AGS-M4-4)
 * M4: model tier badge → picker (AC-AGS-M4-7)
 * M5: artifacts sheet badge (AC-M5-3)
 * M5: session file upload button (AC-M5-4)
 * M5: "Use as…" menu on assistant messages (AC-M5-7)
 * M5: promoted entities section (AC-M5-8)
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useSocketEvent } from '@/hooks/useSocket';
import { useParams, useNavigate } from 'react-router-dom';
import { Bot } from 'lucide-react';
import { t } from '@/lib/i18n';
import { RorosPage } from '@/components/roros/RorosPage';
import { agentSessionsService } from '@/services/roros/agent-sessions.service';
import type { AgentSessionDTO } from '@/services/roros/agent-sessions.service';
import { ArtifactsSheet } from './components/ArtifactsSheet';
import { InspectorTab } from './InspectorTab';
import { usePermissions } from '@/hooks/usePermissions';
import { PERMISSIONS } from '@/constants/permissions';
import { useRorosActor } from '@/hooks/roros/actors';
import {
  InspectorBoundary,
  type ChatMessage,
} from './components/AgentChatPageParts';
import { AgentChatSidebar } from './components/AgentChatSidebar';
import { AgentChatHeader } from './components/AgentChatHeader';
import { AgentChatComposer } from './components/AgentChatComposer';
import { AgentChatTranscript } from './components/AgentChatTranscript';
import { useAgentSessionUpload } from './hooks/useAgentSessionUpload';
import { useAgentSessionTitleRename } from './hooks/useAgentSessionTitleRename';
import { useAgentSessionSlashAutocomplete } from './hooks/useAgentSessionSlashAutocomplete';
import { useAgentSessionRoster } from './hooks/useAgentSessionRoster';
import { mapAgentSessionMessages } from './agentSessionMessageMapping';
import { useAgentSessionList } from './hooks/useAgentSessionList';
import { useAgentSessionArtifacts } from './hooks/useAgentSessionArtifacts';
import { useAgentSessionUsage } from './hooks/useAgentSessionUsage';
import { useAgentSessionActions } from './hooks/useAgentSessionActions';
import { useAgentSessionMessageStream } from './hooks/useAgentSessionMessageStream';
import { useAgentChatViewState } from './hooks/useAgentChatViewState';

// ─── Main page ─────────────────────────────────────────────────────────────────

export function AgentChatPage() {
  // AC-AGS-M4-5: when navigated to /roros/agent-sessions/:id, auto-open that session
  const { id: sessionIdFromRoute } = useParams<{ id?: string }>();
  const navigate = useNavigate();

  const { hasPermission } = usePermissions();
  const isAdmin = hasPermission(PERMISSIONS.ROROS_ACTORS_MANAGE);

  const [selectedActorId, setSelectedActorId] = useState<string>('');
  const [activeSession, setActiveSession] = useState<AgentSessionDTO | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [startError, setStartError] = useState<string | null>(null);

  // AC-M5-8: promoted entities refresh token
  const [promotionRefreshToken, setPromotionRefreshToken] = useState(0);

  // AC-M6-6: confirmation resolution refresh token (incremented after approve/reject)
  const [confirmationRefreshToken, setConfirmationRefreshToken] = useState(0);

  const abortRef = useRef<boolean>(false);

  // AC-CHAT-12 (fix): resolve the active session's real operator to get imageAttachmentId.
  // Called unconditionally at top level; hook is `enabled` only when id is truthy.
  const activeActorQuery = useRorosActor(activeSession?.actorId ?? '');
  const {
    uploadRef,
    isUploading,
    uploadChipMsg,
    handleFileUpload,
  } = useAgentSessionUpload({ activeSessionId: activeSession?.id });

  const {
    sessions,
    isLoadingSessions,
    sidebarSearch,
    setSidebarSearch,
    prependSession,
    replaceSession,
  } = useAgentSessionList();
  const {
    runningInputTokens,
    runningOutputTokens,
    runningCostUsd,
    modelTierOverride,
    setModelTierOverride,
    syncUsageFromSession,
    syncCountersFromSession,
    syncTierFromSession,
    addUsageFromChunk,
  } = useAgentSessionUsage();

  const handleSessionRenamed = useCallback((updated: AgentSessionDTO) => {
    setActiveSession(updated);
    replaceSession(updated);
  }, [replaceSession]);

  const titleRename = useAgentSessionTitleRename({
    activeSession,
    onSessionRenamed: handleSessionRenamed,
  });

  const slashAutocomplete = useAgentSessionSlashAutocomplete({
    activeSessionId: activeSession?.id,
  });

  const { roster, actorOptions, getActorName } = useAgentSessionRoster();
  const { activeTab, setActiveTab, bottomRef } = useAgentChatViewState({
    activeSessionId: activeSession?.id,
    messages,
  });
  const { isStreaming, sendMessage } = useAgentSessionMessageStream({
    activeSession,
    inputText: slashAutocomplete.inputText,
    clearInput: slashAutocomplete.clearInput,
    inputRef: slashAutocomplete.inputRef,
    setActiveSession,
    setMessages,
    replaceSession,
    addUsageFromChunk,
    syncCountersFromSession,
    abortRef,
  });
  const { showArtifacts, setShowArtifacts, artifactsCount } = useAgentSessionArtifacts(
    activeSession?.id,
    isStreaming,
  );
  const {
    startSession,
    openSession,
    refetchMessages,
    cancelSession,
  } = useAgentSessionActions({
    selectedActorId,
    activeSession,
    setActiveSession,
    setMessages,
    setStartError,
    setConfirmationRefreshToken,
    prependSession,
    replaceSession,
    syncUsageFromSession,
    syncCountersFromSession,
    syncTierFromSession,
    abortRef,
  });

  // AC-AGS-M4-5: auto-open session from URL param (when navigated to /roros/agent-sessions/:id)
  useEffect(() => {
    if (!sessionIdFromRoute) return;
    agentSessionsService.getSession(sessionIdFromRoute).then((detail) => {
      setActiveSession(detail.session);
      syncTierFromSession(detail.session);
      setMessages(mapAgentSessionMessages(detail.messages, { includeSystem: true }));
    }).catch(() => {
      // non-fatal — user will see empty state
    });
  }, [sessionIdFromRoute, syncTierFromSession]);

  // AC-M6-6: listen for tool_confirmation_request socket events → refresh messages
  useSocketEvent('session:tool_confirmation_request', useCallback((data: { sessionId: string }) => {
    if (activeSession && data.sessionId === activeSession.id) {
      refetchMessages();
    }
  }, [activeSession, refetchMessages]));

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape' && slashAutocomplete.showAutocomplete) {
      slashAutocomplete.closeAutocomplete();
      return;
    }
    if (e.key === 'Enter' && !e.shiftKey && !slashAutocomplete.showAutocomplete) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <RorosPage
      title={t('Agent Chat')}
      icon={<Bot className="h-5 w-5" aria-hidden />}
    >
      <div className="flex h-[calc(100vh-10rem)] gap-4">

        <AgentChatSidebar
          selectedActorId={selectedActorId}
          onSelectedActorIdChange={setSelectedActorId}
          actorOptions={actorOptions}
          roster={roster}
          onStartSession={startSession}
          startError={startError}
          sidebarSearch={sidebarSearch}
          onSidebarSearchChange={setSidebarSearch}
          onOpenDetailedSearch={() => navigate('/roros/agent-sessions')}
          isLoadingSessions={isLoadingSessions}
          sessions={sessions}
          activeSessionId={activeSession?.id ?? null}
          onOpenSession={openSession}
        />

        {/* ── Main Chat Area ────────────────────────────────────────────────── */}
        <div className="flex-1 flex flex-col min-w-0">
          {!activeSession && (
            <div className="flex-1 flex items-center justify-center" data-testid="agent-chat-setup">
              <div className="text-center text-slate-400">
                <Bot className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                <p className="text-sm">{t('Select an operator and start a new session, or pick an existing one from the list.')}</p>
              </div>
            </div>
          )}

          {activeSession && (
            <>
              <AgentChatHeader
                activeSession={activeSession}
                activeActor={activeActorQuery.data}
                actorName={getActorName(activeSession.actorId)}
                artifactsCount={artifactsCount}
                onOpenArtifacts={() => setShowArtifacts(true)}
                activeTab={activeTab}
                onActiveTabChange={setActiveTab}
                isAdmin={isAdmin}
                isTitleEditing={titleRename.isTitleEditing}
                titleDraft={titleRename.titleDraft}
                onTitleDraftChange={titleRename.setTitleDraft}
                titleInputRef={titleRename.titleInputRef}
                onTitleKeyDown={titleRename.handleTitleKeyDown}
                onSaveTitle={() => void titleRename.saveTitleRename()}
                isTitleSaving={titleRename.isTitleSaving}
                onStartTitleEdit={titleRename.startTitleEdit}
                onCancelTitleEdit={titleRename.cancelTitleEdit}
                onCancelSession={cancelSession}
              />

              {/* AC-M7-6: Inspector tab content */}
              {activeTab === 'inspector' && (
                <div className="flex-1 overflow-y-auto" data-testid="inspector-panel">
                  {/* AC-CHAT-4: InspectorBoundary ensures a render-time throw
                      is contained here; tab buttons above are never unmounted. */}
                  <InspectorBoundary>
                    <InspectorTab sessionId={activeSession.id} isAdmin={isAdmin} />
                  </InspectorBoundary>
                </div>
              )}

              {/* Message list + input — hidden when inspector tab active */}
              {activeTab !== 'inspector' && <>
              <AgentChatTranscript
                sessionId={activeSession.id}
                messages={messages}
                promotionRefreshToken={promotionRefreshToken}
                onPromoted={() => setPromotionRefreshToken((n) => n + 1)}
                onResolved={refetchMessages}
                bottomRef={bottomRef}
              />

              <AgentChatComposer
                activeSession={activeSession}
                modelTierOverride={modelTierOverride}
                runningInputTokens={runningInputTokens}
                runningOutputTokens={runningOutputTokens}
                runningCostUsd={runningCostUsd}
                onTierChanged={(tier, resolvedModel) => {
                  setModelTierOverride(tier);
                  setActiveSession((prev) => {
                    if (!prev) return prev;
                    return {
                      ...prev,
                      modelTierOverride: tier,
                      // AC-CHAT-2: update displayed model to the backend-resolved value
                      ...(resolvedModel ? { model: resolvedModel } : {}),
                    };
                  });
                }}
                uploadChipMsg={uploadChipMsg}
                showAutocomplete={slashAutocomplete.showAutocomplete}
                skills={slashAutocomplete.skills}
                skillQuery={slashAutocomplete.skillQuery}
                onSelectSkill={slashAutocomplete.selectSkill}
                uploadRef={uploadRef}
                isUploading={isUploading}
                onFileUpload={handleFileUpload}
                inputRef={slashAutocomplete.inputRef}
                inputText={slashAutocomplete.inputText}
                onInputChange={slashAutocomplete.handleInputChange}
                onInputKeyDown={handleKeyDown}
                isStreaming={isStreaming}
                onSendMessage={sendMessage}
              />
              </>}
            </>
          )}
        </div>
      </div>

      {/* AC-M5-3: artifacts slide-in sheet (portal-like fixed overlay) */}
      {activeSession && showArtifacts && (
        <ArtifactsSheet
          sessionId={activeSession.id}
          onClose={() => setShowArtifacts(false)}
        />
      )}
    </RorosPage>
  );
}
